import { Fragment, useMemo } from 'react';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { formatters } from '@frontend/shared';
import { theme } from '@frontend/theme';
import { useModalControl } from '@frontend/design-system';
import { useAverageProductionAmount, useHasAdminAccess } from '../hooks';
import { EditAverageProductionModal } from './edit-average-production-modal';
import { StatItem, StatItemData } from './stat-item';
import { StatsMetric } from './stats-metric';

interface RoiProductionValueStatsMetricProps {
  appointmentsCount?: number;
  isLoading?: boolean;
  title?: string;
}

const widthWithEdit = 130;
const widthWithoutEdit = 100;

export const RoiProductionValueStatsMetric = ({
  appointmentsCount = 0,
  isLoading,
  title,
}: RoiProductionValueStatsMetricProps) => {
  const { t } = useTranslation('analytics');
  const hasAdminAccess = useHasAdminAccess();

  const { averageProductionAmount, isLoading: isLoadingAverageProductionAmount } = useAverageProductionAmount();
  const { openModal: openEditAppValueModal, modalProps: editAppValueModalProps } = useModalControl();

  const updatedStats: StatItemData[] = useMemo(() => {
    const avgStatWidth = hasAdminAccess ? widthWithEdit : widthWithoutEdit;
    const restStatWidth = `calc((100% - ${avgStatWidth}px) / 2 - ${theme.spacing(4)})`;

    return [
      {
        label: t('Appointments Completed'),
        value: formatters.value(appointmentsCount),
        infoHoverText: t('Total completed appointments'),
        style: {
          width: restStatWidth,
        },
      },
      {
        label: t('Average Appt Value'),
        value: formatters.shortenCurrency(averageProductionAmount.value),
        infoHoverText: t('Average appointment value'),
        style: {
          width: avgStatWidth,
        },
        ...(hasAdminAccess
          ? {
              onEditClick: openEditAppValueModal,
              editIconTooltipText: t("Adjust the average appointment value to better reflect your practice's revenue."),
            }
          : {}),
      },

      {
        label: t('Production Value'),
        value: formatters.shortenCurrency(appointmentsCount * averageProductionAmount.value),
        infoHoverText: t(
          'Production Value is calculated by multiplying the total number of completed appointments by the average appointment value'
        ),
        style: {
          width: restStatWidth,
        },
      },
    ];
  }, [appointmentsCount, averageProductionAmount, hasAdminAccess, t, openEditAppValueModal]);

  return (
    <>
      <StatsMetric
        dataPointsStyle={{
          alignItems: 'center',
        }}
        isLoading={isLoading || isLoadingAverageProductionAmount}
        title={title || t('Production Value')}
        uniqueId='roi-production-value-stats-metric'
      >
        {updatedStats?.map(({ label, isLoading: isLoadingItem, ...rest }, index) => (
          <Fragment key={label}>
            <StatItem isLoading={isLoadingItem || isLoading} label={label} {...rest} />
            {index === 0 && <Icon name='x' color='subdued' size={16} />}
            {index === 1 && <Icon name='equal' color='subdued' size={16} />}
          </Fragment>
        ))}
      </StatsMetric>

      {hasAdminAccess && <EditAverageProductionModal {...editAppValueModalProps} baseTrackingId='roi-production' />}
    </>
  );
};
